import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { useWhiteLabelStore } from '@/stores/whiteLabelStore';
import { useNotifications } from '@/services/notifications';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pageTitles = {
  '/dashboard': 'Dashboard Real-Time',
  '/dashboard/incidents': 'Alertas',
  '/dashboard/incident-board': 'Incidentes',
  '/dashboard/incident-history': 'Histórico de Alertas',
  '/dashboard/maintenance': 'Manutenção',
  '/dashboard/hostgroups': 'Grupo de Hosts',
  '/dashboard/hosts': 'Hosts',
  '/dashboard/top-triggers': 'Top Triggers',
  '/dashboard/availability': 'Disponibilidade',
  '/dashboard/period': 'Dashboard por Período',
  '/dashboard/reports': 'Relatórios',
  '/dashboard/status': 'Status Page',
  '/dashboard/heatmap': 'Mapa de Calor',
  '/dashboard/sla-dashboard': 'Dashboard de SLA',
  '/dashboard/capacity': 'Dashboard de Capacidade',
  '/dashboard/executive': 'Dashboard Executivo',
  '/dashboard/monthly': 'Comparativo Mensal',
  '/dashboard/settings': 'Configurações',
  '/dashboard/categories': 'Categorias',
  '/dashboard/sla-config': 'Configuração de SLA',
  '/dashboard/saved-filters': 'Filtros Salvos',
  '/dashboard/users': 'Gestão de Usuários',
  '/dashboard/profile': 'Meu Perfil',
  '/dashboard/groups': 'Grupos de Usuários',
  '/dashboard/oncall': 'Gestão de Plantão (OnCall)',
  '/dashboard/integrations': 'Integrações',
  '/dashboard/aiops/agents': 'Agentes AI',
  '/dashboard/aiops/analysis': 'Análise Completa',
  '/dashboard/aiops/granular': 'Análise Granular',
  '/dashboard/aiops/historical': 'Análise de Histórico',
  '/dashboard/aiops/correlation': 'Correlação de Eventos',
  '/dashboard/aiops/prediction': 'Predição',
  '/dashboard/aiops/rca': 'RCA Automático',
  '/dashboard/aiops/health': 'Score de Saúde',
  '/dashboard/aiops/anomalies': 'Detecção de Anomalias',
  '/dashboard/aiops/history': 'Histórico de Análises AI',
  '/dashboard/kb/solutions': 'Soluções Técnicas',
  '/dashboard/kb/recommendations': 'Recomendações',
  '/dashboard/zabbix-config': 'Configuração Zabbix API',
  '/dashboard/scheduled-reports': 'Relatórios Agendados',
  '/dashboard/daily-summary': 'Resumo Executivo',
};

function IncidentPoller({ getAuthHeader }) {
  const { checkNewIncidents, enabled } = useNotifications(getAuthHeader);
  const intervalRef = useRef(null);
  const enabledRef = useRef(enabled);
  const getAuthRef = useRef(getAuthHeader);
  const checkRef = useRef(checkNewIncidents);

  // Keep refs updated without triggering re-renders
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { getAuthRef.current = getAuthHeader; }, [getAuthHeader]);
  useEffect(() => { checkRef.current = checkNewIncidents; }, [checkNewIncidents]);

  useEffect(() => {
    const poll = async () => {
      if (!enabledRef.current) return;
      try {
        const res = await axios.get(`${API}/incidents?limit=20`, { headers: getAuthRef.current() });
        const incidents = (res.data?.incidents || res.data || []).map(i => ({
          id: i.eventid || i.id,
          name: i.name || i.description,
          host_name: i.host_name || i.hosts?.[0]?.name || '',
          severity: parseInt(i.severity || 0)
        }));
        checkRef.current(incidents);
      } catch (_) {}
    };
    poll();
    intervalRef.current = setInterval(poll, 60000);
    return () => clearInterval(intervalRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

export function DashboardLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { isAuthenticated, getAuthHeader, token } = useAuthStore();
  const { initTheme } = useThemeStore();
  const { fetchConfig, loaded: wlLoaded } = useWhiteLabelStore();

  useEffect(() => {
    initTheme();
  }, [initTheme]);

  useEffect(() => {
    if (isAuthenticated && !wlLoaded) {
      fetchConfig(getAuthHeader());
    }
  }, [isAuthenticated, wlLoaded, fetchConfig, getAuthHeader]);

  // Auto-refresh JWT token every 25 minutes to keep session alive
  useEffect(() => {
    if (!isAuthenticated || !token) return;
    const refreshToken = async () => {
      try {
        const res = await axios.post(`${API}/auth/refresh`, {}, { headers: getAuthHeader() });
        if (res.data.token) {
          useAuthStore.setState({ token: res.data.token });
        }
      } catch (_) {}
    };
    const interval = setInterval(refreshToken, 25 * 60 * 1000); // 25 min
    return () => clearInterval(interval);
  }, [isAuthenticated, token, getAuthHeader]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const title = pageTitles[location.pathname] || 'OneView';

  return (
    <div className="min-h-screen bg-background">
      <IncidentPoller getAuthHeader={getAuthHeader} />
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className={cn(
        "transition-all duration-300",
        "ml-0 md:ml-16",
        !collapsed && "md:ml-64"
      )}>
        <Topbar title={title} />
        <main className="p-3 md:p-6" data-testid="dashboard-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
